import { getLocalStorage, setLocalStorage } from 'src/functions/localStorageHandler';
import { getSessionPageCount } from './pageState';
import { buildInteractionEvent, pushToGA4, getScrollPercent, getGA4SessionId } from './trackInteractionCore';

const QUALITY_SESSION_KEY_PREFIX = '@analytics:quality_session_fired';

const MIN_PAGES = 3;
const MIN_SCROLL_PERCENT = 50;

let maxScrollPercent = 0;
let hasInteracted = false;
let scrollListener: (() => void) | null = null;

// Scope the dedup key to the GA4 session_id so the guard survives new tabs and
// browser close/reopen within the same session (sessionStorage is tab-scoped,
// which was causing duplicate firings under a single GA4 session_id).
const getDedupKey = (sessionId: string): string => `${QUALITY_SESSION_KEY_PREFIX}:${sessionId}`;

const hasFired = (sessionId: string): boolean => getLocalStorage(getDedupKey(sessionId)) === 'true';

const fireQualitySession = (triggers: string[]): void => {
  // Events without a session_id land in GA4's "(not set)" bucket and cannot be
  // attributed to a session. Bots and clients without the _ga_ cookie cause
  // repeated firings there. Skip them entirely.
  const sessionId = getGA4SessionId();
  if (!sessionId) return;

  if (hasFired(sessionId)) return;

  setLocalStorage(getDedupKey(sessionId), 'true');

  const eventData = buildInteractionEvent(
    'quality-session',
    'session',
    'quality_session',
  );

  eventData.interaction_text = triggers.join(', ');
  eventData.event = 'quality_session';

  pushToGA4(eventData);
};

const checkQualitySignals = (): void => {
  const sessionId = getGA4SessionId();
  if (!sessionId || hasFired(sessionId)) return;

  const triggers: string[] = [];

  if (getSessionPageCount() >= MIN_PAGES) {
    triggers.push(`${MIN_PAGES}+ pages`);
  }

  if (maxScrollPercent >= MIN_SCROLL_PERCENT) {
    triggers.push(`${MIN_SCROLL_PERCENT}%+ scroll`);
  }

  if (hasInteracted) {
    triggers.push('meaningful interaction');
  }

  if (triggers.length >= 2) {
    fireQualitySession(triggers);
  }
};

const handleScroll = (): void => {
  const percent = getScrollPercent();
  if (percent > maxScrollPercent) {
    maxScrollPercent = percent;
  }
  checkQualitySignals();
};

export const markMeaningfulInteraction = (): void => {
  hasInteracted = true;
  checkQualitySignals();
};

export const initQualitySessionTracking = (): void => {
  if (typeof window === 'undefined') return;

  if (scrollListener) {
    window.removeEventListener('scroll', scrollListener);
  }

  scrollListener = handleScroll;
  window.addEventListener('scroll', scrollListener, { passive: true });
};

export const checkQualitySessionOnRouteUpdate = (): void => {
  checkQualitySignals();
};
